import { useAuthUser } from '@/src/hooks/auth';
import { useCurrentDeviceStore } from '@/src/modules/pricing/hooks/useCurrentDeviceStore';
import { useIsRevenueCatAvailable } from '@/src/modules/pricing/hooks/useIsRevenueCatAvailable';
import { useMutationRevenueCatPurchase } from '@/src/modules/pricing/mutations/useMutationRevenueCatPurchase';
import { useMutationWebPurchase } from '@/src/modules/pricing/mutations/useMutationWebPurchase';
import useQueryRevenueCatCustomerInfo from '@/src/modules/pricing/queries/useQueryRevenueCatCustomerInfo';
import { getSubscriptionAccess } from '@/src/modules/pricing/utils/getSubscriptionAccess';
import {
  PurchaseError,
  PurchaseErrorCode,
  type PurchaseType,
} from '@fabric-app/business/pricing/pricing.types';
import {
  getFullTierFromPlan,
  getFullTierFromUser,
  getPlanByOfferingId,
  isPlanWithDurationBelow,
} from '@fabric-app/business/pricing/pricing.utils';
import { useQuerySubscriptionKey } from '@fabric-app/business/pricing/queries/useQuerySubscription';
import { toast } from '@fabric-app/business/store/alerts';
import { BillingPeriod } from '@fabric-app/types/pricing/pricing.types';
import { ensureError } from '@fabric-app/utils/guards';
import { SubscriptionTier } from '@fabric/woody-client';
import type { PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { PACKAGE_TYPE, PRORATION_MODE } from '@revenuecat/purchases-capacitor';
import { captureException } from '@sentry/nextjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export type MutationPurchaseVariables = {
  aPackage: PurchasesPackage;
};

const revenueCatMismatch = [
  'revenuecat_user_mismatch',
  'revenuecat_unavailable',
  'revenuecat_missing_entitlement',
  'revenuecat_subscription_mismatch_woody',
];

export const mutationKeyPurchase = ['subscription-purchase'];
/**
 * Will purchase a package using RevenueCat (subscription) or the web and update the user's subscription.
 */
export const useMutationPurchase = () => {
  const user = useAuthUser();
  const { refetch: refetchCustomerInfo } = useQueryRevenueCatCustomerInfo({
    enabled: false,
  });
  const isRevenueCatAvailable = useIsRevenueCatAvailable();
  const currentDeviceStore = useCurrentDeviceStore();
  const subscriptionQueryKey = useQuerySubscriptionKey();
  const mutationRevenueCatPurchase = useMutationRevenueCatPurchase();
  const mutationWebPurchase = useMutationWebPurchase();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: mutationKeyPurchase,
    mutationFn: async ({ aPackage }: MutationPurchaseVariables): Promise<void> => {
      const plan = getPlanByOfferingId(aPackage.presentedOfferingContext.offeringIdentifier);
      if (!plan) throw new PurchaseError(PurchaseErrorCode.GENERIC, currentDeviceStore);
      if (!user) throw new PurchaseError(PurchaseErrorCode.GENERIC, currentDeviceStore);

      if (user.subscription.tier === SubscriptionTier.LIFETIME)
        throw new PurchaseError(PurchaseErrorCode.HAS_LIFETIME, currentDeviceStore);

      const billingCycle: BillingPeriod =
        aPackage.packageType === PACKAGE_TYPE.MONTHLY
          ? BillingPeriod.Monthly
          : BillingPeriod.Yearly;

      const userTier = getFullTierFromUser(user);
      const planTier = getFullTierFromPlan(plan, billingCycle);

      if (userTier === planTier)
        throw new PurchaseError(PurchaseErrorCode.SAME_PLAN, currentDeviceStore);

      // planTier < userTier means the user is downgrading
      const isDowngrade = isPlanWithDurationBelow(planTier, userTier);

      const optimisticUserData = {
        tier: plan.tier,
        billingCycle,
      };

      const purchaseType: PurchaseType =
        userTier === 'free' ? 'first' : isDowngrade ? 'downgrade' : 'upgrade';

      if (isRevenueCatAvailable) {
        const customerInfo = (await refetchCustomerInfo()).data;
        if (!customerInfo) throw new PurchaseError(PurchaseErrorCode.GENERIC, currentDeviceStore);

        const purchaseAccess = getSubscriptionAccess({
          user,
          customerInfo,
          isRevenueCatAvailable,
          currentDeviceStore,
        });

        if (
          purchaseAccess.canAlterSubscription.allowed === false &&
          revenueCatMismatch.includes(purchaseAccess.canAlterSubscription.reason)
        )
          throw new PurchaseError(PurchaseErrorCode.NATIVE_TIER_MISMATCH, currentDeviceStore);
        else if (purchaseAccess.canAlterSubscription.allowed === false)
          throw new PurchaseError(PurchaseErrorCode.GENERIC, currentDeviceStore);

        // Plan ID is needed for switching plans on Google Play Store, Apple handles it automatically
        const previousPlanId = purchaseAccess.userEntitlement
          ? `${purchaseAccess.userEntitlement.productIdentifier}:${purchaseAccess.userEntitlement.productPlanIdentifier}`
          : null;

        await mutationRevenueCatPurchase.mutateAsync({
          options: {
            aPackage,
            googleProductChangeInfo:
              // If there is a previous plan ID and the plan type is different, we need to pass the old plan ID
              // if it's the same plan type, we don't need to pass the old plan ID it will automatically handle it
              previousPlanId && plan.tier !== user.subscription.tier
                ? {
                    oldProductIdentifier: previousPlanId,
                    prorationMode: isDowngrade
                      ? PRORATION_MODE.DEFERRED
                      : PRORATION_MODE.IMMEDIATE_AND_CHARGE_PRORATED_PRICE,
                  }
                : undefined,
          },
          type: purchaseType,
          optimistic: optimisticUserData,
        });
      } else {
        await mutationWebPurchase.mutateAsync({
          priceId: aPackage.product.identifier,
          type: purchaseType,
          optimistic: optimisticUserData,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionQueryKey });
    },
    onError: (e) => {
      captureException(ensureError(e));

      if (e instanceof PurchaseError) {
        if (e.code === PurchaseErrorCode.CANCELLED) return;

        toast({
          content: e.message,
        });
      } else {
        toast({
          content: new PurchaseError(PurchaseErrorCode.GENERIC, currentDeviceStore).message,
        });
      }
    },
  });

  return mutation;
};
